import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Boxes, UtensilsCrossed, Refrigerator } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const DRAWERS_HERO = "/products/drawers/731077_5d9e25809aea44d998cf28168b514f84~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ");

// Already-native products elsewhere on the site — cross-linked inline within their matching
// section below, not duplicated, so this page shows the full range without a confusing
// catch-all section. Keyed by SECTIONS id.
const SECTION_LINKED_PRODUCTS = {
  "drawers": [
    { name: "ONCA Suzuki Jimny Drawers with Aluminium Table", price: 3899, thumb: "/products/bullbars/731077_ee5e682d4eb745549df1ea2ada679879~mv2.jpg", to: "/bull-bars/onca-jimny-drawers" },
  ],
  "kitchens": [
    { name: "6 Person Full Dinner Set in Ammo Box", price: 339, thumb: "/products/drawers/731077_e524143d8ac34dd4bf8a9de4fc49ceff~mv2.webp", to: "/camping-accessories/6-person-dinner-set-ammo-box" },
  ],
  "fridge-slides": [
    { name: "Bushtech Fridge/Cargo Slide for Jimny", price: 1095, thumb: "/products/jimny/731077_b355ac6560b948d1b23fa949c7315ca9~mv2.jpg", to: "/jimny-store/bushtech-fridge-cargo-slide-jimny" },
  ],
  "tables": [
    { name: "EEZI-AWN 3-in-1", price: 115, thumb: "/products/roofracks/731077_f23c140cadc5422cb6741ef91150f9cd~mv2.jpg", to: "/camping-accessories/eezi-awn-3-in-1" },
  ],
  "storage": [
    { name: "Clear Top Canvas Storage Bags", price: 74.95, thumb: "/products/drawers/731077_456b25abb0b64e6cbdfa25bd0d756101~mv2.webp", to: "/camping-accessories/clear-top-canvas-storage-bags" },
  ],
};

const SECTIONS = [
  { id: "drawers", label: "Drawer Systems", heading: "BUILT TO CARRY IT ALL.", category: "drawers" },
  { id: "kitchens", label: "Kitchens", heading: "COOK PROPERLY, ANYWHERE.", category: "kitchen" },
  { id: "fridge-slides", label: "Fridge Slides", heading: "ACCESS WITHOUT THE STRAIN.", category: "fridge-slide" },
  { id: "tables", label: "Tables", heading: "INSTANT SETUP, EVERY TIME.", category: "table" },
  { id: "storage", label: "Storage & Accessories", heading: "EVERYTHING ELSE, SORTED.", category: "storage" },
];

const WHY = [
  { icon: Boxes, t: "Real Drawer Engineering", d: "Aluminium & stainless-steel systems built for constant loaded use, not flatpack furniture-grade slides." },
  { icon: UtensilsCrossed, t: "Kitchens That Actually Work", d: "Quick Pitch's pullout kitchens fold, drop and stack intelligently — cook standing up, pack down in seconds." },
  { icon: Refrigerator, t: "Proven Fridge Slides", d: "EEZI-AWN and Quick Pitch slides rated to carry heavy loads day after day, on real corrugations." },
];

const priceLabel = (m) => {
  if (m.priceOnRequest) return "REQUEST QUOTE";
  return `${m.priceFrom ? "FROM " : ""}$${fmt(m.price)}`;
};

const Card = ({ m, testid }) => (
  <Link
    to={`/drawers-kitchens-tables/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {m.badge && (
        <div className="absolute top-4 left-4">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">{m.badge}</span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <p className="font-display text-xl text-[var(--brand-primary)] leading-none">{priceLabel(m)}</p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

const LinkedCard = ({ p, testid }) => (
  <Link
    to={p.to}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <img src={p.thumb} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight">{p.name.toUpperCase()}</h3>
        <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-3">${fmt(p.price)}</p>
      </div>
    </div>
  </Link>
);

export default function DrawersPage() {
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/drawer`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="drawers-page">
      <SEO
        path="/drawers-kitchens-tables"
        title="Drawer Systems, Kitchens & Fridge Slides NZ · Bushtech & Quick Pitch"
        description="Overland drawer systems, pullout kitchens, fridge slides and tables from Bushtech, Quick Pitch and EEZI-AWN. From $235 to $4,690."
        image={DRAWERS_HERO}
      />

      <section className="relative min-h-[80svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={DRAWERS_HERO} alt="Overland kitchen and drawer system" className="w-full h-full object-cover" style={{ objectPosition: "50% 40%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— Drawers, Kitchens & Tables</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.88] text-white hero-text-shadow max-w-[18ch]">
            YOUR RIG.<br />
            <span className="text-[var(--brand-primary)]">PROPERLY</span> ORGANISED.
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              Drawer systems, pullout kitchens, fridge slides and tables from Bushtech, Quick Pitch and EEZI-AWN — built for constant loaded use, not weekend camping.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              {SECTIONS.slice(0, 3).map((s) => (
                <a key={s.id} href={`#${s.id}`} data-testid={`drawers-cta-${s.id}`} className="btn-secondary">{s.label}</a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} data-testid={`drawers-why-${i}`} className="bg-[var(--bg-main)] p-6 md:p-8">
                <Icon size={22} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
                <h3 className="font-display text-lg md:text-xl text-white mb-2">{w.t}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{w.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {SECTIONS.map((section, si) => {
        const items = products.filter((m) => m.type === section.category);
        if (items.length === 0) return null;
        return (
          <section
            key={section.id}
            id={section.id}
            className={`container-x pb-16 md:pb-20 scroll-mt-24 ${si > 0 ? "pt-16 md:pt-20 border-t border-[var(--border-default)]" : ""}`}
          >
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="overline mb-3">— {section.label}</p>
                <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">{section.heading}</h2>
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((m, i) => <Card key={m.slug} m={m} testid={`${section.id}-item-${i}`} />)}
            </div>
            {(SECTION_LINKED_PRODUCTS[section.id] || []).length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {SECTION_LINKED_PRODUCTS[section.id].map((p, i) => (
                  <LinkedCard key={p.to} p={p} testid={`${section.id}-linked-${i}`} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
